import { stacks } from "@dropbox/api-v2-client";
import { Badge } from "@dropbox/dig-components/badge";
import { Button } from "@dropbox/dig-components/buttons";
import { Card } from "@dropbox/dig-components/card";
import { Text } from "@dropbox/dig-components/typography";
import { Split } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import {
  AiChatLine,
  CloseLine,
  CreateStackLine,
  StacksLine,
  Twinkle2Line,
} from "@dropbox/dig-icons/dist/mjs/assets";
import { PAP_Complete_DashShoppingCart } from "@mirage/analytics/events/types/complete_dash_shopping_cart";
import {
  AskWithMultiDocsRouterState,
  ConversationType,
  CtaSource,
} from "@mirage/service-conversation/router_state_types";
import { buildConversationRouterState } from "@mirage/service-conversation/utils/buildConversationRouterState";
import { reportPapEvent } from "@mirage/service-product-logging";
import { DashTruncateWithTooltip } from "@mirage/shared/util/DashTruncateWithTooltip";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import { useShowStackChooserModal } from "@mirage/stacks/StackChooser/StackChooser";
import i18n from "@mirage/translations";
import { RoutePath } from "@mirage/webapp/routeTypes";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CurateTooltip } from "./CurateTooltip";
import { useResultsShoppingCart } from "./hooks/useResultsShoppingCart";
import styles from "./ResultsShoppingCart.module.css";
import { ShoppingCartItemTiles } from "./ShoppingCartItemTiles";

interface ResultsShoppingCartProps {
  searchTerm: string;
  selectAllCallback?: () => void;
  showEmptyState?: boolean;
  searchSessionId?: string;
  searchQueryUuid?: string;
}

export default function ResultsShoppingCart({
  searchTerm,
  selectAllCallback,
  showEmptyState,
  searchSessionId,
  searchQueryUuid,
}: ResultsShoppingCartProps) {
  const {
    cartItems,
    setIsActivated,
    clearShoppingCart,
    isCurateTooltipShown,
    setIsCurateTooltipShown,
  } = useResultsShoppingCart();
  const [isDismissed, setIsDismissed] = useState<boolean>(false);
  const [showCurateTooltip, setShowCurateTooltip] = useState<boolean>(false);

  const createStackButtonRef = useRef(null);

  const navigate = useNavigate();

  const showStackChooserModal = useShowStackChooserModal(navigate);

  const navigateToChat = useCallback(() => {
    setIsActivated(true);
    const askWithPromptParams: AskWithMultiDocsRouterState = {
      chatSources: Array.from(cartItems.values())
        .filter((item) => item.searchResult.summarizable === "yes_summarizable")
        .map((item) => ({
          type: "mirage_search_result",
          searchResult: item.searchResult,
        })),
      scenarioMeta: {
        type: "search",
        searchQueryUuid: searchQueryUuid,
        searchSessionId: searchSessionId,
        query: searchTerm,
      },
      ctaSource: CtaSource.SerpPageResultsChat,
      conversationType: ConversationType.AskWithMultiDocs,
    };

    const routerState = buildConversationRouterState(
      CtaSource.SerpPageResultsChat,
      askWithPromptParams,
    );
    navigate(RoutePath.CHAT, { state: routerState });
    reportPapEvent(
      PAP_Complete_DashShoppingCart({
        selectionLength: cartItems.size,
        dashActionType: "chat",
      }),
    );
  }, [navigate, reportPapEvent, cartItems, clearShoppingCart, setIsActivated]);

  const addToStack = useCallback(() => {
    const itemsToAdd = Array.from(cartItems.values())
      .filter((item) => {
        if (!item.searchResult.url) {
          return false;
        }
        return true;
      })
      .map((item) => ({
        url: item.searchResult.url ?? "",
        metadata_title: item.searchResult.title,
        branded_type: item.searchResult.brandedType ?? undefined,
        id_3p: item.searchResult.id3p ?? undefined,
      }));
    showStackChooserModal({
      callback: (stack?: stacks.Stack) => {
        if (stack) {
          clearShoppingCart(true);
          reportPapEvent(
            PAP_Complete_DashShoppingCart({
              selectionLength: cartItems.size,
              dashActionType: "add_to_stack",
            }),
          );
        }
      },
      searchTerm: searchTerm,
      url: itemsToAdd[0].url,
      items: Array.from(cartItems.values())
        .filter((item) => {
          if (!item.searchResult.url) {
            return false;
          }
          return true;
        })
        .map((item) => ({
          url: item.searchResult.url ?? "",
          metadata_title: item.searchResult.title,
          branded_type: item.searchResult.brandedType ?? undefined,
          id_3p: item.searchResult.id3p ?? undefined,
        })),
    });
  }, [showStackChooserModal, cartItems, reportPapEvent]);

  const clearState = useCallback(() => {
    clearShoppingCart();
    setIsDismissed(true);
  }, [clearShoppingCart, setIsDismissed]);

  useEffect(() => {
    if (showEmptyState && !isCurateTooltipShown) {
      setShowCurateTooltip(true);
      setIsCurateTooltipShown(true);
    }
  }, [
    showEmptyState,
    isCurateTooltipShown,
    setIsCurateTooltipShown,
    setShowCurateTooltip,
  ]);

  const closeCurateTooltip = useCallback(() => {
    setShowCurateTooltip(false);
  }, [setIsCurateTooltipShown, setShowCurateTooltip]);

  useEffect(() => {
    if (cartItems.size > 0) {
      setShowCurateTooltip(false);
    }
  }, [cartItems.size]);

  useEffect(() => {
    if (isCurateTooltipShown) {
      setShowCurateTooltip(false);
    }
  }, [searchTerm]);

  const summarizableCartItemsCount = useMemo(() => {
    return Array.from(cartItems.values()).filter(
      (item) => item.searchResult.summarizable === "yes_summarizable",
    ).length;
  }, [cartItems]);

  const summarizeTooltipText = useMemo(() => {
    const cartItemsDiff = cartItems.size - summarizableCartItemsCount;
    if (cartItemsDiff === 0) return "";
    if (cartItemsDiff === 1) return i18n.t("item_not_summarizable");
    return i18n.t("items_not_summarizable", { count: cartItemsDiff });
  }, [cartItems.size, summarizableCartItemsCount]);

  if (cartItems.size === 0 && (!showEmptyState || isDismissed)) {
    return <></>;
  }

  const closeButton = (
    <Split.Item>
      <Button variant="borderless" onClick={clearState}>
        <UIIcon src={CloseLine} />
      </Button>
    </Split.Item>
  );

  const content =
    cartItems.size === 0 ? (
      <Split gap="20" alignY="center" paddingX="12" paddingY="8">
        <Split.Item>
          <Split alignY="center" gap="8">
            <UIIcon src={Twinkle2Line} />
            <Text size="small" variant="label" style={{ whiteSpace: "nowrap" }}>
              {i18n.t("shopping_cart_description")}
            </Text>
          </Split>
        </Split.Item>
        <Split.Item>
          <Split gap="10" alignY="center">
            <UIIcon src={StacksLine} />
            <Text
              size="large"
              variant="label"
              className={styles.ResultsShoppingCartStackName}
              isBold
            >
              <DashTruncateWithTooltip
                lines={1}
                tooltipProps={{ title: searchTerm }}
              >
                {searchTerm}
              </DashTruncateWithTooltip>
            </Text>
          </Split>
        </Split.Item>
        <Split.Item>
          <Button
            ref={createStackButtonRef}
            variant="outline"
            onClick={selectAllCallback}
            withIconStart={<UIIcon src={CreateStackLine} />}
          >
            {i18n.t("create_stack")}
          </Button>
        </Split.Item>
        {closeButton}
      </Split>
    ) : (
      <Split gap="20" alignY="center" paddingX="12" paddingY="8">
        <Split.Item>
          <Split gap="10" alignY="center">
            <UIIcon src={StacksLine} />
            <Text
              size="large"
              variant="label"
              className={styles.ResultsShoppingCartStackName}
            >
              <DashTruncateWithTooltip
                lines={1}
                tooltipProps={{ title: searchTerm }}
              >
                {searchTerm}
              </DashTruncateWithTooltip>
            </Text>
            <Text>{cartItems.size}</Text>
          </Split>
        </Split.Item>
        <Split.Item>
          <ShoppingCartItemTiles cartItems={cartItems} />
        </Split.Item>
        <Split.Item>
          <Split gap="10">
            <Split.Item>
              <Text />
            </Split.Item>
            <Split.Item>
              {summarizableCartItemsCount > 0 && (
                <DigTooltip
                  title={summarizeTooltipText}
                  isPortaled
                  openDelay={400}
                >
                  <Button
                    variant="primary"
                    onClick={navigateToChat}
                    withIconStart={<UIIcon src={AiChatLine} />}
                  >
                    <Split gap="6" alignY="center">
                      {i18n.t("chat")}
                      {summarizeTooltipText && (
                        <Badge variant="attention" size="xsmall" />
                      )}
                    </Split>
                  </Button>
                </DigTooltip>
              )}
            </Split.Item>
            <Split.Item>
              <Button
                variant="primary"
                onClick={addToStack}
                withIconStart={<UIIcon src={CreateStackLine} />}
              >
                {i18n.t("shopping_cart_save_stack")}
              </Button>
            </Split.Item>
            {closeButton}
          </Split>
        </Split.Item>
      </Split>
    );
  return (
    <>
      <div className={styles.ResultsShoppingCartContainer}>
        <Card className={styles.ResultsShoppingCartCard} hasBorders={false}>
          <Card.Content className={styles.ResultsShoppingCartCardContent}>
            {content}
          </Card.Content>
        </Card>
      </div>
      <div className={styles.ResultsShoppingCartPopoverContainer}>
        {showCurateTooltip && <CurateTooltip onClose={closeCurateTooltip} />}
      </div>
    </>
  );
}
